// SingleBusinessPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  getFirestore,
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  query,
  where,
  getDocs,
  Timestamp,
  runTransaction,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  uploadString,
  getDownloadURL,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import "dayjs/locale/es";

// Assets e íconos
import bgApp from "../assets/bg-app.png";
import logo from "../assets/logo.png";
import cobroGif from "../assets/cobro.gif";
import cargandoNegocio from "../assets/cargandoNegocio.gif";
import errorImage from "../assets/error-negocio.png";
import { FaArrowLeft, FaPlus } from "react-icons/fa";
import { IoQrCode } from "react-icons/io5";
import { FaWhatsapp } from "react-icons/fa6";

const SingleBusinessPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const db = getFirestore();
  const storage = getStorage();
  const auth = getAuth();

const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);


  // Estados generales y específicos
  const [business, setBusiness] = useState(null);
  const [agentLocation, setAgentLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPaidToday, setHasPaidToday] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReactivateAlert, setShowReactivateAlert] = useState(false);
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [extraConcept, setExtraConcept] = useState("");
  const [extraAmount, setExtraAmount] = useState("");
  const [saveConcept, setSaveConcept] = useState(false);
  const [conceptSuggestions, setConceptSuggestions] = useState([]);
  const [extraTotal, setExtraTotal] = useState(0);
  const [extraCosts, setExtraCosts] = useState([]);
  const [showRecargaModal, setShowRecargaModal] = useState(false);
  const [recargaAmount, setRecargaAmount] = useState("");
  const [showAbonoModal, setShowAbonoModal] = useState(false);
  const [abonoAmount, setAbonoAmount] = useState("");
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [captureAction, setCaptureAction] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [modoAdeudo, setModoAdeudo] = useState(false);
  // Estado para almacenar la foto capturada para reporte
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;


  const videoRef = useRef(null);


const handleDeleteTransaction = async (trans) => {
  try {
    if (!business) {
      toast.error("No se encontró el negocio asociado.");
      return;
    }

    if (
      !window.confirm(
        "¿Seguro que deseas eliminar esta transacción? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }

    // Solo permitimos borrar cobros y abonos desde este flujo
    if (
      !(
        trans.collection === "cobros" ||
        trans.tipo === "abono"
      )
    ) {
      toast.error("Solo se pueden eliminar cobros y abonos desde aquí.");
      return;
    }

    const transRef = doc(db, trans.collection, trans.id);

    // Eliminamos ÚNICAMENTE el documento de la transacción
    await runTransaction(db, async (transaction) => {
      transaction.delete(transRef);
    });

    // Actualizar lista local en el historial
    setTransactions((prev) => prev.filter((t) => t.id !== trans.id));

    // Recalcular si hoy sigue teniendo algún cobro registrado
    try {
      const now = new Date();
      const startOfDay = Timestamp.fromDate(
        new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
      );
      const endOfDay = Timestamp.fromDate(
        new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
      );

      const paymentsQuery = query(
        collection(db, "cobros"),
        where("businessId", "==", business.id),
        where("date", ">=", startOfDay),
        where("date", "<=", endOfDay)
      );
      const snapshot = await getDocs(paymentsQuery);
      setHasPaidToday(!snapshot.empty);
    } catch (e) {
      console.warn("No se pudo recalcular estado de pago del día:", e);
    }

    // Importante: NO tocamos saldo ni adeudo del negocio
    toast.success("Transacción eliminada correctamente.");
  } catch (error) {
    console.error("Error al eliminar transacción:", error);
    toast.error(error.message || "No se pudo eliminar la transacción.");
  }
};

  

  // ------------------------------------------------------------------
  // Función para obtener el siguiente folio (para cobros)
  const getNextFolio = async () => {
    const counterDocRef = doc(db, "counters", "cobrosCounter");
    let newFolio = 1;
    await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterDocRef);
      if (!counterDoc.exists()) {
        transaction.set(counterDocRef, { lastFolio: 1 });
        newFolio = 1;
      } else {
        const lastFolio = counterDoc.data().lastFolio;
        newFolio = lastFolio + 1;
        transaction.update(counterDocRef, { lastFolio: newFolio });
      }
    });
    return newFolio;
  };

  // ------------------------------------------------------------------
  // Función para descargar el QR del negocio
  const downloadQR = (qrUrl, name) => {
    const link = document.createElement("a");
    link.href = qrUrl;
    link.download = `${name}_QR.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ------------------------------------------------------------------
  // Helper: si hoy no está en schedule.days, retorna el campo para agregarlo
  const getScheduleDayUpdate = () => {
    const raw = dayjs().locale("es").format("dddd");
    const today = raw.charAt(0).toUpperCase() + raw.slice(1);
    const currentDays = business?.schedule?.days || [];
    if (!currentDays.includes(today)) {
      return { "schedule.days": [...currentDays, today] };
    }
    return {};
  };

  // ------------------------------------------------------------------
  // Función para realizar el cobro normal
const handlePayment = async () => {
  if (!business || !agentLocation) return;
  setIsGeneratingReceipt(true);

  try {
    const user = auth.currentUser;
    const agentName = user.displayName || user.email || "Agente";
    const folioNumber = await getNextFolio();
    const formattedFolio = folioNumber.toString().padStart(4, "0");

    const totalAmount =
      Number(business.quota || 0) + extraTotal + Number(business.adeudo || 0);
    const availableCredit = Number(business.saldo || 0);
    let appliedCredit = 0;
    let netAmount = totalAmount;
    if (availableCredit > 0) {
      if (availableCredit >= totalAmount) {
        appliedCredit = totalAmount;
        netAmount = 0;
      } else {
        appliedCredit = availableCredit;
        netAmount = totalAmount - availableCredit;
      }
    }

    // Datos de la transacción (sin receiptUrl aún)
    const transactionData = {
      businessId: business.id,
      agentId: user.uid,
      agentName,
      folio: formattedFolio,
      date: Timestamp.now(),
      totalAmount,
      appliedCredit,
      netAmount,
      location: { Lat: agentLocation.latitude, Lng: agentLocation.longitude },
      businessPhone: business.phone,
      businessName: business.name,
      ownerName: business.owner || "Dueño no definido",
      receiptUrl: "" // Se actualizará en background
    };

    // Actualización del negocio
    const nuevoSaldo = availableCredit - appliedCredit;
    const updateData = { saldo: nuevoSaldo, adeudo: 0, ...getScheduleDayUpdate() };
    if (!business.location && agentLocation) {
      updateData.location = `Lat: ${agentLocation.latitude}, Lng: ${agentLocation.longitude}`;
    }

    // Guardar cobro + actualizar negocio EN PARALELO
    const [docRef] = await Promise.all([
      addDoc(collection(db, "cobros"), transactionData),
      updateDoc(doc(db, "negocios", business.id), updateData),
    ]);
    setBusiness((prev) => ({ ...prev, ...updateData }));

    // NAVEGAR INMEDIATAMENTE - el usuario ve el recibo al instante
    setIsGeneratingReceipt(false);
    navigate("/recibo", { state: { transaction: { id: docRef.id, ...transactionData } } });

    // La generación del recibo y envío de WhatsApp se hacen en PaymentReceipt
  } catch (error) {
    console.error("Error al procesar el cobro:", error);
    toast.error("Error al procesar el cobro. Intenta de nuevo.");
    setIsGeneratingReceipt(false);
  }
};

  // ------------------------------------------------------------------
  // Función para registrar un abono (se incluye ownerName)
  const handleAbono = async () => {
    if (!business || !agentLocation || !abonoAmount) return;
    try {
      setIsProcessing(true);
      const user = auth.currentUser;
      const agentName = user.displayName || user.email || "Agente";
      const folioNumber = await getNextFolio();
      const formattedFolio = folioNumber.toString().padStart(4, "0");
      const abonoValue = Number(abonoAmount);
      const currentDebt = Number(business.adeudo || 0);
      const currentSaldo = Number(business.saldo || 0);
      let newDebt = currentDebt;
      let remainder = 0;
      if (currentDebt > 0) {
        if (abonoValue <= currentDebt) {
          newDebt = currentDebt - abonoValue;
        } else {
          remainder = abonoValue - currentDebt;
          newDebt = 0;
        }
      } else {
        remainder = abonoValue;
      }

      const ownerName = business.owner || "Dueño no definido";

      const transactionData = {
        businessId: business.id,
        agentId: user.uid,
        agentName,
        folio: formattedFolio,
        date: Timestamp.now(),
        totalAmount: abonoValue,
        appliedCredit: 0,
        netAmount: abonoValue,
        location: { Lat: agentLocation.latitude, Lng: agentLocation.longitude },
        businessPhone: business.phone,
        businessName: business.name,
        ownerName,
        tipo: "abono",
      };

      // Guardar cobro + actualizar negocio EN PARALELO
      const newSaldo = currentSaldo + remainder;
      const [docRef] = await Promise.all([
        addDoc(collection(db, "cobros"), transactionData),
        updateDoc(doc(db, "negocios", business.id), { adeudo: newDebt, saldo: newSaldo, ...getScheduleDayUpdate() }),
      ]);
      setBusiness((prev) => ({ ...prev, adeudo: newDebt, saldo: newSaldo }));

      // NAVEGAR INMEDIATAMENTE
      setIsProcessing(false);
      setShowAbonoModal(false);
      setAbonoAmount("");
      navigate("/recibo", { state: { transaction: { id: docRef.id, ...transactionData, receiptUrl: "" } } });

      // La generación del recibo y envío de WhatsApp se hacen en PaymentReceipt
    } catch (error) {
      console.error("Error al procesar el abono:", error);
      setIsProcessing(false);
      toast.error("Error al procesar el abono");
    }
  };

  // ------------------------------------------------------------------
  // Función para registrar un adeudo (handleDebtPhoto)
  const handleDebtPhoto = async (photoBlob) => {
    try {
      // 1. Subir la foto de evidencia
      const evidenceStorageRef = ref(storage, `adeudos/${business.id}/evidencia_${Date.now()}.png`);
      await uploadBytes(evidenceStorageRef, photoBlob, { contentType: "image/png" });
      const evidenceURL = await getDownloadURL(evidenceStorageRef);
      const ownerName = business.owner || "Dueño no definido";

      const user = auth.currentUser;
      const agentName = user.displayName || user.email || "Agente";
      const baseAmount = Number(business.quota || 0);
      const newDebtAmount = baseAmount + extraTotal;
      let appliedCredit = 0;
      let debtToRegister = newDebtAmount;
      let remainingSaldo = Number(business.saldo || 0);
      if (remainingSaldo > 0) {
        if (remainingSaldo >= newDebtAmount) {
          appliedCredit = newDebtAmount;
          debtToRegister = 0;
          remainingSaldo -= newDebtAmount;
        } else {
          appliedCredit = remainingSaldo;
          debtToRegister = newDebtAmount - remainingSaldo;
          remainingSaldo = 0;
        }
      }

      // 2. Datos del adeudo (sin receiptURL aún)
      const debtData = {
        businessId: business.id,
        agentId: user.uid,
        agentName,
        date: Timestamp.now(),
        baseAmount,
        extraCosts,
        totalAmount: newDebtAmount,
        appliedCredit,
        debtToRegister,
        location: { Lat: agentLocation.latitude, Lng: agentLocation.longitude },
        evidenceURL,
        receiptURL: "",
        tipo: "adeudo",
        businessPhone: business.phone,
        businessName: business.name,
        ownerName,
      };

      const nuevoAdeudo = (business.adeudo || 0) + debtToRegister;

      // Guardar adeudo + actualizar negocio EN PARALELO
      const [adeudoRef] = await Promise.all([
        addDoc(collection(db, "adeudos"), debtData),
        updateDoc(doc(db, "negocios", business.id), { adeudo: nuevoAdeudo, saldo: remainingSaldo, ...getScheduleDayUpdate() }),
      ]);
      setBusiness((prev) => ({ ...prev, adeudo: nuevoAdeudo, saldo: remainingSaldo }));

      setExtraTotal(0);
      setExtraCosts([]);
      setModoAdeudo(false);

      // NAVEGAR INMEDIATAMENTE
      navigate("/recibo", {
        state: {
          transaction: {
            ...debtData,
            receiptUrl: "",
            dayAmount: debtToRegister,
            totalDebt: nuevoAdeudo,
          },
        },
      });

      // La generación del recibo y envío de WhatsApp se hacen en PaymentReceipt
    } catch (error) {
      console.error("Error al registrar el adeudo:", error);
      toast.error("Error al registrar el adeudo");
    }
  };

  // ------------------------------------------------------------------
  // Función para la acción "No encontrado" (inicia reporte)
  const handleNotFound = async () => {
    setCaptureAction("reporte");
    setShowCamera(true);
    setCapturedPhoto(null);
  };

  // ------------------------------------------------------------------
  // Activar la cámara cuando se muestre el modal
  useEffect(() => {
    if (showCamera) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const constraints = isMobile
        ? { video: { facingMode: { exact: "environment" } } }
        : { video: true };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
        })
        .catch((error) => {
          console.error("Error al activar la cámara:", error);
          toast.error("Error al activar la cámara");
        });
    }
  }, [showCamera]);

  // ------------------------------------------------------------------
  // Capturar la foto y, según la acción, llamar a la función adecuada
  const capturePhoto = async () => {
    if (!videoRef.current || !business || !agentLocation) return;
    const canvas = document.createElement("canvas");
    canvas.width = 640;
    canvas.height = 480;
    const context = canvas.getContext("2d");
    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const stream = videoRef.current.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    canvas.toBlob(async (blob) => {
      if (captureAction === "adeudo") {
        await handleDebtPhoto(blob);
      } else if (captureAction === "reporte") {
        // Convertir el blob a base64 y almacenarlo para vista previa
        const reader = new FileReader();
        reader.onloadend = () => {
          setCapturedPhoto(reader.result);
        };
        reader.readAsDataURL(blob);
      }
    }, "image/png");
  };

  useEffect(() => {
  const beforeUnloadHandler = (e) => {
    if (isGeneratingReceipt) {
      e.preventDefault();
      e.returnValue = "¡El recibo aún se está generando! No cierres la aplicación.";
      return e.returnValue;
    }
  };
  window.addEventListener("beforeunload", beforeUnloadHandler);
  return () => window.removeEventListener("beforeunload", beforeUnloadHandler);
}, [isGeneratingReceipt]);

  // ------------------------------------------------------------------
  // Función para enviar el reporte con la foto capturada (vista previa)
  const handleSendReportPhoto = async () => {
    if (!capturedPhoto) {
      toast.error("Debes capturar una fotografía antes de enviar el reporte.");
      return;
    }
    if (isUploadingPhoto) return;
    try {
      setIsUploadingPhoto(true);
      const user = auth.currentUser;

      // Subir foto y geocoding EN PARALELO (geocoding no bloquea)
      const storageRefReporte = ref(storage, `reportes/${business.id}/${Date.now()}.png`);
      const [, photoURL] = await Promise.all([
        uploadString(storageRefReporte, capturedPhoto.split(",")[1], "base64", { contentType: "image/png" }),
        getDownloadURL(storageRefReporte).catch(() => ""),
      ]).then(async () => {
        const url = await getDownloadURL(storageRefReporte);
        return [null, url];
      });

      // Guardar reporte sin esperar geocoding
      const reportData = {
        businessId: business.id,
        agentId: user.uid,
        date: Timestamp.now(),
        location: { Lat: agentLocation.latitude, Lng: agentLocation.longitude },
        address: "",
        photoURL,
      };

      const updatedReports = (business.reportes || 0) + 1;
      const updatedStatus = updatedReports >= 5 ? "inactivo" : business.status;

      // Guardar reporte + actualizar negocio EN PARALELO
      const [reportRef] = await Promise.all([
        addDoc(collection(db, "reportes"), reportData),
        updateDoc(doc(db, "negocios", business.id), { reportes: updatedReports, status: updatedStatus }),
      ]);
      setBusiness((prev) => ({ ...prev, reportes: updatedReports, status: updatedStatus }));
      setCapturedPhoto(null);
      setCaptureAction(null);
      setShowCamera(false);
      toast.success("Reporte enviado exitosamente");
      if (updatedReports >= 5) {
        alert("El negocio ahora está inactivo por múltiples reportes.");
      }

      // Geocoding FIRE-AND-FORGET: actualiza la dirección en background
      axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${agentLocation.latitude}&lon=${agentLocation.longitude}`
      ).then((geoRes) => {
        const address = geoRes.data?.display_name || "";
        if (address) {
          updateDoc(doc(db, "reportes", reportRef.id), { address }).catch(() => {});
        }
      }).catch(() => {});
    } catch (error) {
      console.error("Error al enviar el reporte:", error);
      toast.error("Error al enviar el reporte");
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // ------------------------------------------------------------------
  // Función para registrar un costo extra
  const handleExtraCostSubmit = async () => {
    if (!extraConcept || !extraAmount) {
      toast.error("Debe ingresar concepto y cantidad");
      return;
    }
    try {
      const user = auth.currentUser;
      const timestamp = new Date().toISOString();
      const extraCostData = {
        businessId: business.id,
        agentId: user.uid,
        concept: extraConcept,
        amount: Number(extraAmount),
        date: timestamp,
      };
      await addDoc(collection(db, "costosExtras"), extraCostData);
      if (saveConcept) {
        const conceptQuery = query(
          collection(db, "conceptosExtra"),
          where("concept", "==", extraConcept)
        );
        const conceptSnapshot = await getDocs(conceptQuery);
        if (conceptSnapshot.empty) {
          await addDoc(collection(db, "conceptosExtra"), { concept: extraConcept });
        }
      }
      setExtraTotal((prev) => prev + Number(extraAmount));
      setExtraCosts((prev) => [
        ...prev,
        { concept: extraConcept, amount: Number(extraAmount) },
      ]);
      setExtraConcept("");
      setExtraAmount("");
      setSaveConcept(false);
      setShowExtraModal(false);
      toast.success("Costo extra registrado correctamente");
    } catch (error) {
      console.error("Error al registrar costo extra:", error);
      toast.error("Error al registrar costo extra");
    }
  };

  // ------------------------------------------------------------------
  // Función para recargar saldo
  const handleRecarga = async () => {
    if (!recargaAmount || Number(recargaAmount) <= 0) {
      toast.error("Ingrese un monto válido para recargar");
      return;
    }
    try {
      const montoRecarga = Number(recargaAmount);
      const user = auth.currentUser;
      const timestamp = Timestamp.now();
      const currentDebt = Number(business.adeudo || 0);
      const currentSaldo = Number(business.saldo || 0);
      let nuevoAdeudo = currentDebt;
      let nuevoSaldo = currentSaldo;
      if (currentDebt > 0) {
        if (montoRecarga >= currentDebt) {
          nuevoAdeudo = 0;
          nuevoSaldo += (montoRecarga - currentDebt);
        } else {
          nuevoAdeudo = currentDebt - montoRecarga;
        }
      } else {
        nuevoSaldo += montoRecarga;
      }
      const recargaData = {
        businessId: business.id,
        agentId: user.uid,
        amount: montoRecarga,
        date: timestamp,
      };
      await addDoc(collection(db, "recargas"), recargaData);
      const ingresoDiarioData = {
        businessId: business.id,
        agentId: user.uid,
        amount: currentDebt > 0 ? Math.max(0, montoRecarga - currentDebt) : montoRecarga,
        date: timestamp,
      };
      await addDoc(collection(db, "ingresosDiarios"), ingresoDiarioData);
      await updateDoc(doc(db, "negocios", business.id), { adeudo: nuevoAdeudo, saldo: nuevoSaldo });
      setBusiness((prev) => ({ ...prev, adeudo: nuevoAdeudo, saldo: nuevoSaldo }));
      setRecargaAmount("");
      setShowRecargaModal(false);
      toast.success("Saldo recargado exitosamente");
    } catch (error) {
      console.error("Error al recargar saldo:", error);
      toast.error("Error al recargar saldo");
    }
  };

  // --- Helper para reintentar requests ---
async function sendWithRetry(requestFn, maxAttempts = 3, delay = 2000) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await requestFn();
      // Modifica la validación según tu API (puede ser .success, .status, etc)
      if (result?.data?.status === "success" || result?.status === 200) {
        return result;
      } else {
        throw new Error("Respuesta no exitosa de la API");
      }
    } catch (err) {
      if (attempt === maxAttempts) throw err;
      await new Promise(res => setTimeout(res, delay));
    }
  }
}

  // ------------------------------------------------------------------
  // Función para obtener el historial de transacciones
  const fetchTransactionHistory = async () => {
    try {
      const cobrosQuery = query(
        collection(db, "cobros"),
        where("businessId", "==", business.id)
      );
      const cobrosSnapshot = await getDocs(cobrosQuery);
      const cobros = cobrosSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        collection: "cobros",
      }));
      const adeudosQuery = query(
        collection(db, "adeudos"),
        where("businessId", "==", business.id)
      );
      const adeudosSnapshot = await getDocs(adeudosQuery);
      const adeudos = adeudosSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        collection: "adeudos",
      }));
      const combined = [...cobros, ...adeudos].sort((a, b) => {
        const dateA = a.date.seconds ? a.date.seconds : new Date(a.date).getTime() / 1000;
        const dateB = b.date.seconds ? b.date.seconds : new Date(b.date).getTime() / 1000;
        return dateB - dateA;
      });
      setTransactions(combined);
    } catch (error) {
      console.error("Error al obtener historial de transacciones:", error);
      toast.error("Error al cargar el historial");
    }
  };

// Función utilitaria para formatear el número
function toWhatsAppJid(phone) {
  let num = String(phone).replace(/\D/g, "");
  if (num.length === 10) return "521" + num;
  if (num.length === 12 && num.startsWith("52") && num[2] !== "1")
    return "521" + num.slice(2);
  if (num.length === 13 && num.startsWith("521")) return num;
  return num;
}

function formatDateMx(dateValue) {
  const date = dateValue?.seconds ? new Date(dateValue.seconds * 1000) : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("es-MX");
}

async function fetchWhatsAppConfig(db) {
  const configRef = doc(db, "config", "whatsapp");
  const configSnap = await getDoc(configRef);
  if (!configSnap.exists()) return null;
  const data = configSnap.data() || {};
  return {
    apiToken: data.wa_api_token || "",
    phoneId: data.wa_phone_id || "",
    templateName: data.wa_template_name || "",
    templateLang: data.wa_template_lang || "es_MX",
    sendMode: data.wa_send_mode || "qr",
  };
}

async function sendCloudTemplate({ config, phone, imageUrl, bodyParams }) {
  const payload = {
    messaging_product: "whatsapp",
    to: phone,
    type: "template",
    template: {
      name: config.templateName,
      language: { code: config.templateLang || "es_MX" },
      components: [
        {
          type: "body",
          parameters: bodyParams.map((value) => ({
            type: "text",
            text: String(value ?? ""),
          })),
        },
      ],
    },
  };

  if (imageUrl) {
    payload.template.components.unshift({
      type: "header",
      parameters: [
        {
          type: "image",
          image: { link: imageUrl },
        },
      ],
    });
  }

  const url = `https://graph.facebook.com/v21.0/${config.phoneId}/messages`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${config.apiToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errPayload = await response.json().catch(() => ({}));
    throw new Error(errPayload?.error?.message || "Error al enviar plantilla.");
  }

  return response.json().catch(() => ({}));
}

// ...tu función de reenvío
const handleResendReceipt = async (receiptUrl, businessPhone, businessName, type, date) => {
  try {
    if (!receiptUrl || !businessPhone) {
      toast.error("No se encontró la URL del comprobante o el teléfono del cliente.");
      return;
    }

    const phoneFormatted = toWhatsAppJid(businessPhone);
    const bodyParams = [businessName || "", type || "", formatDateMx(date)];

    const config = await fetchWhatsAppConfig(db);
    if (!config) {
      toast.error("No hay configuracion de WhatsApp.");
      return;
    }
    if (!config.apiToken || !config.phoneId || !config.templateName) {
      toast.error("Falta configurar WhatsApp API.");
      return;
    }
    if (config.sendMode === "qr") {
      toast.error("El modo QR requiere backend. Cambia a plantilla o aleatorio.");
      return;
    }

    await sendCloudTemplate({
      config,
      phone: phoneFormatted,
      imageUrl: receiptUrl,
      bodyParams,
    });

    toast.success("Recibo reenviado exitosamente.");
  } catch (error) {
    console.error("Error al reenviar el recibo:", error);
    toast.error("No se pudo reenviar el recibo.");
  }
};



  useEffect(() => {
    if (showHistoryModal && business) {
      fetchTransactionHistory();
    }
  }, [showHistoryModal, business]);

  useEffect(() => {
    if (showExtraModal) {
      const fetchSuggestions = async () => {
        try {
          const suggestionsSnapshot = await getDocs(collection(db, "conceptosExtra"));
          const suggestions = suggestionsSnapshot.docs.map((doc) => doc.data().concept);
          setConceptSuggestions(suggestions);
        } catch (error) {
          console.error("Error al obtener sugerencias de conceptos:", error);
        }
      };
      fetchSuggestions();
    }
  }, [showExtraModal]);

  useEffect(() => {
    const fetchBusinessData = async () => {
      try {
        const businessDoc = await getDoc(doc(db, "negocios", businessId));
        if (businessDoc.exists()) {
          setBusiness({ id: businessDoc.id, ...businessDoc.data() });
        } else {
          console.error("Negocio no encontrado.");
        }
      } catch (error) {
        console.error("Error al obtener el negocio:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBusinessData();
  }, [businessId, db]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setAgentLocation({ latitude, longitude });
        },
        (err) => {
          console.error("Error al obtener ubicación del agente:", err);
        }
      );
    }
  }, []);

  useEffect(() => {
    if (business) {
      const checkPaymentStatus = async () => {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).toISOString();
        const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();
        const paymentsQuery = query(
          collection(db, "cobros"),
          where("businessId", "==", business.id),
          where("date", ">=", startOfDay),
          where("date", "<=", endOfDay)
        );
        const querySnapshot = await getDocs(paymentsQuery);
        setHasPaidToday(!querySnapshot.empty);
      };
      checkPaymentStatus();
    }
  }, [business]);

  const handleReactivateConfirm = async () => {
    try {
      await updateDoc(doc(db, "negocios", business.id), { status: "activo", reportes: 0 });
      setBusiness((prev) => ({ ...prev, status: "activo", reportes: 0 }));
      setShowReactivateAlert(false);
    } catch (error) {
      console.error("Error al reactivar el negocio:", error);
      setShowReactivateAlert(false);
    }
  };
if (isGeneratingReceipt) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center mx-4 max-w-sm">
        <img src={cargandoNegocio} alt="Cargando..." className="w-24 mb-4" />
        <h2 className="text-xl font-bold mb-2 text-[#861E3D]">Generando recibo...</h2>
        <p className="text-gray-500 text-sm text-center">
          Por favor, no cierres la aplicación ni navegues a otra página.
        </p>
      </div>
    </div>
  );
}

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen" style={{ backgroundImage: `url(${bgApp})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 flex flex-col items-center">
          <img src={cargandoNegocio} alt="Cargando..." className="w-28 h-28" />
          <p className="mt-3 text-white/80 text-sm font-medium">Cargando negocio...</p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen" style={{ backgroundImage: `url(${bgApp})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 flex flex-col items-center mx-4">
          <img src={errorImage} alt="Error" className="w-24 h-24 mb-4 opacity-80" />
          <p className="text-white/80 text-base font-medium mb-4">Negocio no encontrado.</p>
          <button onClick={() => navigate("/cobrador")} className="px-6 py-2.5 bg-white/20 text-white rounded-xl text-sm font-medium hover:bg-white/30 transition-all active:scale-95">
            Regresar al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundImage: `url(${bgApp})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }}>

      {/* Header */}
      <header className="sticky top-0 z-40 w-full px-4 py-3 flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, rgba(112,23,48,0.95), rgba(134,30,61,0.9))", backdropFilter: "blur(10px)" }}>
        <button
          onClick={() => navigate("/cobrador")}
          className="w-10 h-10 rounded-full bg-white/20 text-white flex items-center justify-center hover:bg-white/30 transition-all active:scale-95"
        >
          <FaArrowLeft size={16} />
        </button>
        <img src={logo} alt="Logo" className="h-9 object-contain" />
        <div className="w-10" />
      </header>

      {/* Content */}
      <div className="flex-1 w-full px-4 pt-5 pb-24" style={{ background: "linear-gradient(180deg, rgba(92,3,30,0.8) 0%, rgba(60,2,20,0.9) 100%)" }}>

        {/* Business Info Card */}
        <div className="mx-auto max-w-md mb-5">
          <div className="relative overflow-hidden rounded-2xl p-5 text-center"
            style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.12), rgba(255,255,255,0.04))", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-white/5" />
            <div className="absolute -left-6 -bottom-6 w-20 h-20 rounded-full bg-white/5" />
            <div className="relative">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gradient-to-br from-[#BB7820] to-[#D4922A] text-white rounded-2xl text-xl font-bold mb-3 shadow-lg">
                {business.name.split(" ").map((w) => w.charAt(0)).join("").slice(0, 2)}
              </div>
              <h1 className="text-xl font-bold text-white">{business.name}</h1>
              <p className="text-sm text-white/60 mt-1">{business.owner}</p>

              {business.adeudo > 0 ? (
                <span className="inline-block mt-3 bg-red-500/20 border border-red-400/30 text-red-200 rounded-full px-3 py-1 text-xs font-medium">
                  Adeudo: ${Number(business.adeudo).toFixed(2)}
                </span>
              ) : business.saldo > 0 ? (
                <span className="inline-block mt-3 bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 rounded-full px-3 py-1 text-xs font-medium">
                  Saldo a favor: ${Number(business.saldo).toFixed(2)}
                </span>
              ) : null}

              {business.status === "inactivo" && (
                <div className="mt-3">
                  <span className="inline-block bg-gray-500/20 border border-gray-400/30 text-gray-300 rounded-full px-3 py-1 text-xs font-medium">
                    Negocio Inactivo
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons row */}
        <div className="mx-auto max-w-md flex items-center justify-center gap-3 mb-5">
          <div className="flex items-center gap-2 bg-white/10 border border-white/10 text-white px-4 py-2.5 rounded-xl text-sm">
            <FaWhatsapp className="text-green-400" />
            <span>{business.phone}</span>
          </div>
          {business.qrUrl && (
            <button onClick={() => downloadQR(business.qrUrl, business.name)} className="w-11 h-11 bg-white/10 border border-white/10 text-white rounded-xl flex items-center justify-center hover:bg-white/20 transition-all active:scale-95">
              <IoQrCode size={18} />
            </button>
          )}
          <div className="relative">
            <button onClick={() => setIsDropdownOpen((prev) => !prev)} className="w-11 h-11 bg-white/10 border border-white/10 text-white rounded-xl flex items-center justify-center hover:bg-white/20 transition-all active:scale-95">
              <FaPlus size={14} />
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 overflow-hidden">
                <button onClick={() => { setShowExtraModal(true); setIsDropdownOpen(false); }} className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full transition-colors">
                  Costo Extra
                </button>
                <button onClick={() => { setModoAdeudo(true); setIsDropdownOpen(false); }} className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full transition-colors">
                  Registrar Adeudo
                </button>
                <button onClick={() => { setShowAbonoModal(true); setIsDropdownOpen(false); }} className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full transition-colors">
                  Registrar Abono
                </button>
                <button onClick={() => { setShowHistoryModal(true); setIsDropdownOpen(false); }} className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 w-full transition-colors">
                  Historial
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Extras summary */}
        {extraTotal > 0 && (
          <div className="mx-auto max-w-md mb-4">
            <div className="bg-amber-500/15 border border-amber-400/20 rounded-xl px-4 py-2.5 text-center">
              <p className="text-amber-200 text-xs font-medium">Extras incluidos: ${extraTotal.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Activate button for inactive businesses */}
        {business.status === "inactivo" && (
          <div className="mx-auto max-w-md mb-4 text-center">
            <button onClick={() => setShowReactivateAlert(true)} className="px-6 py-2.5 bg-blue-500/20 border border-blue-400/30 text-blue-200 rounded-xl text-sm font-medium hover:bg-blue-500/30 transition-all active:scale-95">
              Activar Negocio
            </button>
          </div>
        )}

        {/* Main action buttons */}
        <div className="mx-auto max-w-md space-y-3">
          {modoAdeudo ? (
            <button
              className="w-full py-4 rounded-2xl text-lg font-bold text-white shadow-lg transition-all active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg, #dc2626, #ef4444)" }}
              onClick={() => { setCaptureAction("adeudo"); setShowCamera(true); }}
            >
              Registrar Adeudo ${(Number(business.quota) + extraTotal).toFixed(2)}
            </button>
          ) : (
            <button
              className={`w-full py-4 rounded-2xl text-lg font-bold text-white shadow-lg transition-all ${
                business.status === "inactivo" || hasPaidToday || isProcessing
                  ? "bg-white/10 text-white/40 cursor-not-allowed"
                  : "active:scale-[0.98]"
              }`}
              style={
                !(business.status === "inactivo" || hasPaidToday || isProcessing)
                  ? { background: "linear-gradient(135deg, #701730, #a3243e)" }
                  : {}
              }
              disabled={business.status === "inactivo" || hasPaidToday || isProcessing}
              onClick={handlePayment}
            >
              {hasPaidToday
                ? "PAGADO"
                : isProcessing
                ? "Procesando..."
                : `COBRAR $${(Number(business.quota) + extraTotal + Number(business.adeudo || 0)).toFixed(2)}`}
            </button>
          )}

          {!hasPaidToday && (
            <button
              className={`w-full py-4 rounded-2xl text-lg font-medium transition-all ${
                business.status === "inactivo" || isProcessing
                  ? "bg-white/10 text-white/40 cursor-not-allowed"
                  : "bg-white/15 border border-white/10 text-white/80 hover:bg-white/20 active:scale-[0.98]"
              }`}
              disabled={business.status === "inactivo" || isProcessing}
              onClick={handleNotFound}
            >
              {isProcessing ? "Procesando..." : "No encontrado"}
            </button>
          )}
        </div>
      </div>

      {/* Camera modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          {captureAction === "reporte" && capturedPhoto ? (
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-4">
              <h2 className="text-base font-bold mb-4 text-center text-gray-800">Vista previa de la fotografía</h2>
              <img src={capturedPhoto} alt="Vista previa" className="w-full h-auto rounded-xl mb-4 border border-gray-200" />
              <div className="flex gap-3">
                <button
                  onClick={() => { setCapturedPhoto(null); setCaptureAction(null); setShowCamera(false); }}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendReportPhoto}
                  disabled={isUploadingPhoto}
                  className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition-colors ${
                    isUploadingPhoto
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  }`}
                >
                  {isUploadingPhoto ? "Subiendo..." : "Enviar Reporte"}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-4">
              <h2 className="text-base font-bold mb-4 text-center text-gray-800">
                {captureAction === "adeudo" ? "Capturar foto para Adeudo" : "Capturar Foto"}
              </h2>
              <video ref={videoRef} className="w-full h-auto rounded-xl mb-4" playsInline></video>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowCamera(false); setCaptureAction(null); setCapturedPhoto(null); }}
                  className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={capturePhoto}
                  className="flex-1 py-2.5 bg-[#701730] text-white rounded-xl font-medium text-sm hover:bg-[#861E3D] transition-colors"
                >
                  Capturar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* History modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen p-4">
            <div className="mx-auto max-w-lg">
              <div className="flex justify-between items-center mb-5 mt-2">
                <h2 className="text-xl font-bold text-white">Historial</h2>
                <button onClick={() => setShowHistoryModal(false)} className="px-4 py-2 bg-white/15 text-white rounded-xl text-sm font-medium hover:bg-white/25 transition-all">
                  Cerrar
                </button>
              </div>

              {transactions.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-white/60 text-sm">No hay transacciones registradas.</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {transactions.map((trans) => (
                    <li key={trans.id} className="bg-white rounded-2xl p-4 shadow-md">
                      <div className="flex justify-between items-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          trans.tipo === "abono"
                            ? "bg-blue-100 text-blue-700"
                            : trans.collection === "adeudos"
                            ? "bg-red-100 text-red-600"
                            : "bg-emerald-100 text-emerald-700"
                        }`}>
                          {trans.tipo === "abono" ? "Abono" : trans.collection === "adeudos" ? "Adeudo" : "Cobro"}
                        </span>
                        <span className="text-[11px] text-gray-400">
                          {(() => {
                            const d = trans.date;
                            let jsDate = null;
                            if (d && typeof d === "object" && "seconds" in d) jsDate = new Date(d.seconds * 1000);
                            else if (typeof d === "string" || typeof d === "number") jsDate = new Date(d);
                            return jsDate && !isNaN(jsDate.getTime()) ? jsDate.toLocaleString() : "-";
                          })()}
                        </span>
                      </div>

                      {trans.folio && (
                        <p className="text-xs text-gray-500 mt-2">Folio: <span className="font-medium text-gray-700">{trans.folio}</span></p>
                      )}

                      <p className="text-lg font-bold text-gray-800 mt-1">
                        ${Number(trans.totalAmount || 0).toFixed(2)}
                      </p>

                      {trans.collection === "adeudos" && (trans.photoURL || trans.evidenceURL) && (
                        <div className="mt-2">
                          <img src={trans.photoURL || trans.evidenceURL} alt="Evidencia" className="w-full h-auto rounded-xl border border-gray-100" />
                        </div>
                      )}

                      {(trans.receiptUrl || trans.receiptURL) && (
                        <div className="mt-3">
                          <button
                            onClick={() => handleResendReceipt(trans.receiptUrl || trans.receiptURL, trans.businessPhone, trans.businessName, trans.tipo || (trans.collection === "adeudos" ? "adeudo" : "cobro"), trans.date)}
                            className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-2 rounded-xl text-xs font-medium hover:bg-emerald-100 transition-colors"
                          >
                            <FaWhatsapp size={14} />
                            Reenviar recibo
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Recarga modal */}
      {showRecargaModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowRecargaModal(false); }}>
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm mx-4">
            <h2 className="text-base font-bold mb-4 text-center text-gray-800">Recargar Saldo</h2>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Monto a recargar</label>
              <input
                type="number"
                value={recargaAmount}
                onChange={(e) => setRecargaAmount(e.target.value)}
                placeholder="$0.00"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#BB7820] text-base"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowRecargaModal(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button onClick={handleRecarga} className="flex-1 py-2.5 bg-[#701730] text-white rounded-xl font-medium text-sm hover:bg-[#861E3D] transition-colors">
                Recargar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Abono modal */}
      {showAbonoModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowAbonoModal(false); setAbonoAmount(""); } }}>
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm mx-4">
            <h2 className="text-base font-bold mb-4 text-center text-gray-800">Registrar Abono</h2>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Monto a abonar</label>
              <input
                type="number"
                value={abonoAmount}
                onChange={(e) => setAbonoAmount(e.target.value)}
                placeholder="$0.00"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#BB7820] text-base"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowAbonoModal(false); setAbonoAmount(""); }} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button onClick={handleAbono} className="flex-1 py-2.5 bg-[#701730] text-white rounded-xl font-medium text-sm hover:bg-[#861E3D] transition-colors">
                Registrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivation modal */}
      {showReactivateAlert && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowReactivateAlert(false); }}>
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm mx-4 text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center bg-blue-100 text-blue-600 rounded-2xl mb-4 text-2xl font-bold">?</div>
            <h2 className="text-base font-bold mb-2 text-gray-800">Confirmar Reactivación</h2>
            <p className="text-sm text-gray-500 mb-5">¿Estás seguro de que deseas reactivar este negocio?</p>
            <div className="flex gap-3">
              <button onClick={() => setShowReactivateAlert(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button onClick={handleReactivateConfirm} className="flex-1 py-2.5 bg-[#701730] text-white rounded-xl font-medium text-sm hover:bg-[#861E3D] transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extra cost modal */}
      {showExtraModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowExtraModal(false); }}>
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-sm mx-4">
            <h2 className="text-base font-bold mb-4 text-center text-gray-800">Registrar Costo Extra</h2>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Concepto</label>
              <input
                type="text"
                value={extraConcept}
                onChange={(e) => setExtraConcept(e.target.value)}
                placeholder="Ingrese concepto"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#BB7820] text-sm"
              />
              {extraConcept && conceptSuggestions.length > 0 && (
                <div className="mt-2 bg-gray-50 p-2 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Sugerencias</p>
                  <ul className="space-y-1">
                    {conceptSuggestions
                      .filter((s) => s.toLowerCase().includes(extraConcept.toLowerCase()))
                      .map((s, index) => (
                        <li key={index} onClick={() => setExtraConcept(s)} className="cursor-pointer text-sm text-gray-700 hover:text-[#861E3D] py-1 px-2 rounded-lg hover:bg-gray-100 transition-colors">
                          {s}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">Cantidad</label>
              <input
                type="number"
                value={extraAmount}
                onChange={(e) => setExtraAmount(e.target.value)}
                placeholder="$0.00"
                className="w-full p-3 border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#BB7820] text-sm"
              />
            </div>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input type="checkbox" checked={saveConcept} onChange={(e) => setSaveConcept(e.target.checked)} className="w-4 h-4 text-[#861E3D] border-gray-300 rounded focus:ring-[#861E3D]" />
              <span className="text-sm text-gray-600">Guardar concepto</span>
            </label>
            <div className="flex gap-3">
              <button onClick={() => setShowExtraModal(false)} className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors">
                Cancelar
              </button>
              <button onClick={handleExtraCostSubmit} className="flex-1 py-2.5 bg-[#701730] text-white rounded-xl font-medium text-sm hover:bg-[#861E3D] transition-colors">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing loader */}
      {isProcessing && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
          <div className="bg-white/10 rounded-2xl p-6">
            <img src={cobroGif} alt="Procesando..." className="w-28 h-28" />
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />

      {/* Photo preview fallback modal */}
      {captureAction === "reporte" && capturedPhoto && !showCamera && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <h2 className="text-base font-bold mb-4 text-center text-gray-800">Vista previa de la fotografía</h2>
            <img src={capturedPhoto} alt="Vista previa" className="w-full h-auto rounded-xl mb-4 border border-gray-200" />
            <div className="flex gap-3">
              <button
                onClick={() => { setCapturedPhoto(null); setCaptureAction(null); setShowCamera(false); }}
                className="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-medium text-sm hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendReportPhoto}
                disabled={isUploadingPhoto}
                className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition-colors ${
                  isUploadingPhoto
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-emerald-600 text-white hover:bg-emerald-700"
                }`}
              >
                {isUploadingPhoto ? "Subiendo..." : "Enviar Reporte"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleBusinessPage;
